import axios from "axios";
import secureAxios from "./axios/secureAxios";
import constants from "../configs/constants";

/**
 * Fetches the current client profile.
 * @returns {Promise<{
 *   success: boolean,
 *   data: {
 *     client_name: string,
 *     client_email: string,
 *     client_balance: number,
 *     client_frozen_balance: number
 *   }
 * }>} Response with client profile info
 */
async function getClientProfile() {
  const { data } = await secureAxios.get("/client/profile");
  return data;
}

/**
 * Fetches all API keys for the current client.
 * @returns {Promise<Array<{
 *   client_api_key_id: string,
 *   api_key_name: string,
 *   description: string|null,
 *   api_key_prefix: string|null,
 *   key_preview: string,
 *   usage_limit_per_minute: number,
 *   is_active: number,
 *   created_at: string|null,
 *   last_used_at: string|null
 * }>>} List of API keys
 */
async function getApiKeys() {
  const { data } = await secureAxios.get("/client/api-keys");
  return data;
}

/**
 * Removes an API key by its client_api_key_id.
 * @param {string} client_api_key_id - The API key's unique identifier
 * @returns {Promise<{
 *   success: boolean,
 *   data: boolean
 * }>} Response with deletion status
 */
async function removeApiKey(client_api_key_id) {
  const { data } = await secureAxios.post("/client/api-keys/delete", {
    client_api_key_id,
  });
  return data;
}

/**
 * Creates a new API key for the client.
 * @param {string} name - Name for the new API key
 * @param {string|null} [description] - Optional description of what the key is used for
 * @returns {Promise<{
 *   success: boolean,
 *   data: {
 *     client_api_key_id: string,
 *     api_key_name: string,
 *     description: string|null,
 *     api_key_prefix: string|null,
 *     usage_limit_per_minute: number,
 *     is_active: number
 *   }
 * }>} Response with the newly created API key
 */
async function createApiKey(name, description) {
  const { data } = await secureAxios.post("/client/api-keys/create", {
    name,
    description,
  });
  return data;
}

/**
 * Updates an API key's name and description.
 * Replace semantics: both fields are overwritten on every call —
 * omitting or passing an empty description clears the stored one.
 * @param {string} client_api_key_id - The API key's unique identifier
 * @param {{ name: string, description?: string|null }} params
 * @returns {Promise<boolean>} true on success
 */
async function updateApiKey(client_api_key_id, { name, description }) {
  const { data } = await secureAxios.post("/client/api-keys/update", {
    client_api_key_id,
    name,
    description,
  });
  return data;
}

/**
 * Disable or re-enable an API key.
 *
 * A disabled key stops authenticating immediately and callers get a distinct
 * `API_KEY_DISABLED` error rather than a generic "Invalid API key".
 *
 * You can act on a key you own, a key belonging to one of your sub-accounts, or
 * a key your parent granted you usage access to — so the key you disable is not
 * necessarily yours, which is why every change is recorded in the audit log.
 *
 * @param {string} client_api_key_id
 * @param {boolean} active - true to enable, false to disable
 * @param {string} [reason] - optional note, stored on the audit event (max 255)
 * @returns {Promise<{success: boolean, changed: boolean, is_active: boolean}>}
 *   `changed: false` means it was already in that state — nothing was written.
 */
async function setApiKeyActive(client_api_key_id, active, reason) {
  const { data } = await secureAxios.post(
    `/client/api-keys/${active ? "enable" : "disable"}`,
    { client_api_key_id, reason }
  );
  return data;
}

/**
 * Audit history for one API key, newest first.
 *
 * `truncated` is true when there are more events than the page returned — the
 * UI must say so rather than presenting a partial log as complete.
 *
 * @param {string} client_api_key_id
 * @returns {Promise<{truncated: boolean, events: Array<{event_id: string,
 *   action: 'disabled'|'enabled', actor_client_id: string, actor_name: string|null,
 *   actor_role: 'owner'|'parent'|'grantee', actor_is_self: boolean,
 *   reason: string|null, created_at: string}>}>}
 */
async function getApiKeyEvents(client_api_key_id) {
  const { data } = await secureAxios.get(
    `/client/api-keys/${client_api_key_id}/events`
  );
  const payload = data?.data ?? data;
  // Tolerate the pre-truncation shape (a bare array) so a uro-web deploy that
  // lands before uro-api doesn't blank the history modal.
  if (Array.isArray(payload)) return { truncated: false, events: payload };
  return {
    truncated: Boolean(payload?.truncated),
    events: Array.isArray(payload?.events) ? payload.events : [],
  };
}

/**
 * Requests topup sessions
 * @param {number} amount - Amount to top up
 * @returns {Promise<{success: boolean, data: string}>} Response with link to topup session
 */
async function requestTopup(amount, { skipAmountOwed = false } = {}) {
  const { data } = await secureAxios.post("/client/top-up", { amount, skipAmountOwed });
  console.log(data);
  return data.data;
}


/**
 * Updates the client profile (name and/or profile image).
 * @param {{ client_name?: string, profile_image_url?: string|null }} params
 * @returns {Promise<{ success: boolean, message: string }>}
 */
async function updateProfile({ client_name, profile_image_url }) {
  const { data } = await secureAxios.post("/client/profile/update", {
    client_name,
    profile_image_url,
  });
  return data;
}

/**
 * Requests an OTP for password change (sent to email).
 * @returns {Promise<{ success: boolean, message: string }>}
 */
async function requestPasswordChangeOtp() {
  const { data } = await secureAxios.post("/client/password/request-otp");
  return data;
}

/**
 * Changes the client password using old password + OTP verification.
 * @param {{ old_password: string, new_password: string, otp: string }} params
 * @returns {Promise<{ success: boolean, message: string }>}
 */
async function changePassword({ old_password, new_password, otp }) {
  const { data } = await secureAxios.post("/client/password/change", {
    old_password,
    new_password,
    otp,
  });
  return data;
}

// ==================== SUB-ACCOUNT ACTIONS ====================

async function getSubAccounts() {
  const { data } = await secureAxios.get("/sub-accounts");
  return data;
}

async function getSubAccountDetail(subId) {
  const { data } = await secureAxios.get(`/sub-accounts/${subId}`);
  return data;
}

async function createSubAccount({ email, name, password, daily_limit, monthly_limit, can_manage_api_keys }) {
  const { data } = await secureAxios.post("/sub-accounts", {
    email,
    name,
    password,
    daily_limit,
    monthly_limit,
    // Opt-in; omitted or falsy means the member cannot manage its own API keys.
    can_manage_api_keys,
  });
  return data;
}

/**
 * Update a sub-account. Every field is optional and only the ones you pass are
 * written — the API applies each with a `!== undefined` check, and JSON drops
 * undefined keys, so omitting a field leaves it untouched.
 *
 * `can_manage_api_keys` lets that member create / rename / delete its OWN API
 * keys. Revoking it stops further changes but does NOT disable keys the member
 * already created.
 *
 * NOTE: the wire field is `client_name`, not `name` — the API destructures
 * `client_name`. This previously sent `name`, which the API silently ignored.
 */
async function updateSubAccount(subId, { name, is_active, can_manage_api_keys }) {
  const { data } = await secureAxios.put(`/sub-accounts/${subId}`, {
    client_name: name,
    is_active,
    can_manage_api_keys,
  });
  return data;
}

/**
 * NOTE: the wire field is `new_password`, not `password` — the API destructures
 * `new_password` and rejects anything shorter than 6 chars. This previously sent
 * `password`, so the reset always failed with "Password must be at least 6
 * characters" regardless of what was typed.
 */
async function resetSubAccountPassword(subId, { password }) {
  const { data } = await secureAxios.put(`/sub-accounts/${subId}/password`, {
    new_password: password,
  });
  return data;
}

async function updateSubAccountBudget(subId, { daily_limit, monthly_limit }) {
  const { data } = await secureAxios.put(`/sub-accounts/${subId}/budget`, {
    daily_limit,
    monthly_limit,
  });
  return data;
}

async function allocateFunds(allocations) {
  const { data } = await secureAxios.post("/sub-accounts/allocate-funds", { allocations });
  return data;
}

async function recallFunds(subId, { amount }) {
  const { data } = await secureAxios.post(`/sub-accounts/${subId}/recall-funds`, { amount });
  return data;
}

async function getSubAccountJobs(subId, { page = 1, limit = 20 } = {}) {
  const { data } = await secureAxios.get(`/sub-accounts/${subId}/jobs`, {
    params: { page, limit },
  });
  return data;
}

async function getTransferHistory({ page = 1, limit = 20 } = {}) {
  const { data } = await secureAxios.get("/sub-accounts/transfers", {
    params: { page, limit },
  });
  return data;
}

async function getSpendingOverview() {
  const { data } = await secureAxios.get("/sub-accounts/spending-overview");
  return data;
}

async function getSubAccountApiKeys(subId) {
  const { data } = await secureAxios.get(`/sub-accounts/${subId}/api-keys`);
  return data;
}

async function createSubAccountApiKey(subId, { name }) {
  const { data } = await secureAxios.post(`/sub-accounts/${subId}/api-keys`, { name });
  return data;
}

async function deleteSubAccountApiKey(subId, keyId) {
  const { data } = await secureAxios.delete(`/sub-accounts/${subId}/api-keys/${keyId}`);
  return data;
}

// ==================== USAGE ACCESS ACTIONS (main account only) ====================

/**
 * Every API key in the organisation, grouped by owning account — the pool the
 * main account can share from. 403s for anyone who is not a main account.
 * @returns {Promise<{
 *   main_account: { client_id: string, client_name: string|null, client_email: string|null, api_keys: Array<object> },
 *   sub_accounts: Array<{ client_id: string, client_name: string, client_email: string, api_keys: Array<object> }>
 * }>}
 */
async function getUsageAccessInventory() {
  const { data } = await secureAxios.get("/sub-accounts/usage-access/keys");
  return data;
}

/**
 * What one sub-account is currently allowed to see.
 * `resolved` reflects what the grants actually evaluate to right now, so a grant
 * pointing at a deleted key shows up as missing rather than silently active.
 * @param {string} subId
 * @returns {Promise<{
 *   client_id: string,
 *   usage_scope: 'own'|'selected'|'all',
 *   grants: Array<{ scope_type: 'account'|'api_key', scope_id: string }>,
 *   resolved: { scope: string, shared_account_count: number, shared_key_count: number }
 * }>}
 */
async function getSubAccountUsageAccess(subId) {
  const { data } = await secureAxios.get(`/sub-accounts/${subId}/usage-access`);
  return data;
}

/**
 * Replace a sub-account's usage access. Full replace — whatever is not sent is
 * revoked. `grants` is only read when usage_scope is 'selected'.
 * @param {string} subId
 * @param {{ usage_scope: 'own'|'selected'|'all', grants?: Array<{scope_type: 'account'|'api_key', scope_id: string}> }} params
 */
async function updateSubAccountUsageAccess(subId, { usage_scope, grants = [] }) {
  const { data } = await secureAxios.put(`/sub-accounts/${subId}/usage-access`, {
    usage_scope,
    grants,
  });
  return data;
}

// ==================== INVITATION ACTIONS ====================

async function sendInvitation({ email, daily_limit, monthly_limit }) {
  const { data } = await secureAxios.post("/sub-accounts/invitations", {
    email,
    daily_limit,
    monthly_limit,
  });
  return data;
}

async function getSentInvitations() {
  const { data } = await secureAxios.get("/sub-accounts/invitations/sent");
  return data;
}

async function cancelInvitation(invitationId) {
  const { data } = await secureAxios.post(`/sub-accounts/invitations/${invitationId}/cancel`);
  return data;
}

async function resendInvitation(invitationId) {
  const { data } = await secureAxios.post(`/sub-accounts/invitations/${invitationId}/resend`);
  return data;
}

async function getReceivedInvitations() {
  const { data } = await secureAxios.get("/invitations/received");
  return data;
}

async function acceptInvitation({ token, invitation_id }) {
  const { data } = await secureAxios.post("/invitations/accept", { token, invitation_id });
  return data;
}

async function rejectInvitation(invitationId) {
  const { data } = await secureAxios.post(`/invitations/${invitationId}/reject`);
  return data;
}

async function getInvitationByToken(token) {
  const { data } = await axios.get(`${constants.API_URL}/invitations/token/${token}`);
  return data;
}

/**
 * @param {string} period
 * @param {string|null} keyId
 * @param {{from: string, to: string}|null} range
 * @param {string|null} viewAs - main accounts only: render the page as this
 *   sub-account sees it. Rejected with 403 for anyone else.
 */
async function getApiUsage(period = '30d', keyId = null, range = null, viewAs = null) {
  // Custom range (from/to as 'YYYY-MM-DD') takes precedence over the preset.
  const params = range && range.from && range.to
    ? { from: range.from, to: range.to }
    : { period };
  if (keyId) params.key_id = keyId;
  if (viewAs) params.view_as = viewAs;
  const { data } = await secureAxios.get('/client/api-usage', { params });
  return data;
}

async function getBillingProfile() {
  const { data } = await secureAxios.get('/client/billing-profile');
  return data;
}

async function saveBillingProfile(profile) {
  const { data } = await secureAxios.post('/client/billing-profile', profile);
  return data;
}

async function getInvoices() {
  const { data } = await secureAxios.get('/client/invoices');
  return data;
}

// Returns a Blob of the invoice PDF (streamed from the authed endpoint).
async function downloadInvoice(id) {
  const res = await secureAxios.get(`/client/invoices/${id}/pdf`, { responseType: 'blob' });
  return res.data;
}

export const clientActions = {
  getClientProfile,
  getBillingProfile,
  saveBillingProfile,
  getInvoices,
  downloadInvoice,
  getApiKeys,
  removeApiKey,
  createApiKey,
  updateApiKey,
  setApiKeyActive,
  getApiKeyEvents,
  requestTopup,
  updateProfile,
  requestPasswordChangeOtp,
  changePassword,
  // Sub-account actions
  getSubAccounts,
  getSubAccountDetail,
  createSubAccount,
  updateSubAccount,
  resetSubAccountPassword,
  updateSubAccountBudget,
  allocateFunds,
  recallFunds,
  getSubAccountJobs,
  getTransferHistory,
  getSpendingOverview,
  getSubAccountApiKeys,
  createSubAccountApiKey,
  deleteSubAccountApiKey,
  // Usage access actions
  getUsageAccessInventory,
  getSubAccountUsageAccess,
  updateSubAccountUsageAccess,
  // Invitation actions
  sendInvitation,
  getSentInvitations,
  cancelInvitation,
  resendInvitation,
  getReceivedInvitations,
  acceptInvitation,
  rejectInvitation,
  getInvitationByToken,
  getApiUsage,
};

export default clientActions;
